import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { money, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Landmark, Pencil, Trash2, HandCoins } from "lucide-react";

const emptyLoan = () => ({ name: "", provider: "Square", original_amount: "", balance: "", interest_rate: "", auto_repay_percent: "", notes: "" });
const emptyRepay = () => ({ amount: "", interest: "", date: new Date().toISOString().slice(0, 10) });

export default function LoansPage() {
  const [loans, setLoans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyLoan());
  const [repayOpen, setRepayOpen] = useState(false);
  const [repayLoan, setRepayLoan] = useState(null);
  const [repay, setRepay] = useState(emptyRepay());

  const load = useCallback(async () => { const { data } = await api.get("/loans"); setLoans(data); }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setR = (k, v) => setRepay((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(emptyLoan()); setOpen(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({ name: l.name, provider: l.provider || "Square", original_amount: String(l.original_amount || ""), balance: String(l.balance || ""), interest_rate: String(l.interest_rate || ""), auto_repay_percent: String(l.auto_repay_percent || ""), notes: l.notes || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) return;
    const payload = {
      name: form.name, provider: form.provider,
      original_amount: parseFloat(form.original_amount) || 0,
      balance: parseFloat(form.balance) || 0,
      interest_rate: parseFloat(form.interest_rate) || 0, notes: form.notes,
      auto_repay_percent: parseFloat(form.auto_repay_percent) || 0,
    };
    if (editing) await api.put(`/loans/${editing.id}`, payload);
    else await api.post("/loans", payload);
    toast.success(editing ? "Loan updated" : "Loan added");
    setOpen(false);
    load();
  };
  const remove = async (id) => { await api.delete(`/loans/${id}`); toast.success("Loan removed"); load(); };

  const openRepay = (l) => { setRepayLoan(l); setRepay(emptyRepay()); setRepayOpen(true); };
  const submitRepay = async () => {
    const amount = parseFloat(repay.amount) || 0;
    if (!amount) { toast.error("Enter a repayment amount"); return; }
    const { data } = await api.post(`/loans/${repayLoan.id}/repay`, { amount, interest: parseFloat(repay.interest) || 0, date: repay.date });
    toast.success(`Repayment logged · balance now ${money(data.balance)}`);
    setRepayOpen(false);
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Loans</h1>
          <p className="text-muted-foreground text-sm mt-1">Track your Square (or other) loan balance. Log repayments — interest is recorded as a claimable expense.</p>
        </div>
        <Button onClick={openNew} data-testid="add-loan-btn" className="gap-2"><Plus className="w-4 h-4" /> Add loan</Button>
      </div>

      {loans.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card px-5 py-12 text-center" data-testid="loans-empty">
          <Landmark className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No loans tracked yet. Square doesn't share loan data with apps, so add yours from your Square Dashboard.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {loans.map((l) => {
            const paid = Math.max((l.original_amount || 0) - (l.balance || 0), 0);
            const pct = l.original_amount ? Math.min((paid / l.original_amount) * 100, 100) : 0;
            return (
              <div key={l.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`loan-card-${l.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold truncate">{l.name}</div>
                    <div className="text-xs text-muted-foreground">{l.provider}{l.interest_rate ? ` · ${l.interest_rate}% p.a.` : ""}{l.auto_repay_percent ? ` · auto ${l.auto_repay_percent}%` : ""}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => openEdit(l)} data-testid={`edit-loan-${l.id}`} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(l.id)} data-testid={`delete-loan-${l.id}`} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">Balance owing</div>
                    <div className="num text-2xl font-bold" data-testid={`loan-balance-${l.id}`}>{money(l.balance)}</div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">of {money(l.original_amount)}</div>
                </div>
                <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{money(paid)} repaid ({pct.toFixed(0)}%)</div>
                <Button variant="outline" size="sm" onClick={() => openRepay(l)} data-testid={`repay-loan-${l.id}`} className="mt-4 w-full gap-2">
                  <HandCoins className="w-4 h-4" /> Log repayment
                </Button>
                {l.repayments?.length > 0 && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    Last: {money(l.repayments[l.repayments.length - 1].amount)} on {fmtDate(l.repayments[l.repayments.length - 1].date)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Loan dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="loan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit loan" : "New loan"}</DialogTitle>
            <DialogDescription>Enter the details from your Square Dashboard (Banking → Loans).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="loan-name" placeholder="Square Loan" />
              </div>
              <div>
                <Label>Provider</Label>
                <Input value={form.provider} onChange={(e) => set("provider", e.target.value)} className="mt-1.5" data-testid="loan-provider" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Original amount</Label>
                <Input type="number" step="0.01" value={form.original_amount} onChange={(e) => set("original_amount", e.target.value)} className="mt-1.5" data-testid="loan-original" placeholder="0.00" />
              </div>
              <div>
                <Label>Current balance</Label>
                <Input type="number" step="0.01" value={form.balance} onChange={(e) => set("balance", e.target.value)} className="mt-1.5" data-testid="loan-balance-input" placeholder="Defaults to original" />
              </div>
            </div>
            <div>
              <Label>Interest rate % <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input type="number" step="0.01" value={form.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} className="mt-1.5" data-testid="loan-rate" placeholder="0" />
            </div>
            <div>
              <Label>Auto-repay % of card takings <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input type="number" step="0.01" min="0" max="100" value={form.auto_repay_percent} onChange={(e) => set("auto_repay_percent", e.target.value)} className="mt-1.5" data-testid="loan-auto-repay" placeholder="e.g. 10" />
              <p className="text-[11px] text-muted-foreground mt-1">Square skims this % of card sales on each import as a repayment.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-loan-btn">{editing ? "Save changes" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repayment dialog */}
      <Dialog open={repayOpen} onOpenChange={setRepayOpen}>
        <DialogContent className="sm:max-w-md" data-testid="repay-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Log a repayment</DialogTitle>
            <DialogDescription>The interest portion is recorded as a claimable expense; the rest reduces the balance.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Repayment amount</Label>
                <Input type="number" step="0.01" value={repay.amount} onChange={(e) => setR("amount", e.target.value)} className="mt-1.5" data-testid="repay-amount" placeholder="0.00" />
              </div>
              <div>
                <Label>of which interest</Label>
                <Input type="number" step="0.01" value={repay.interest} onChange={(e) => setR("interest", e.target.value)} className="mt-1.5" data-testid="repay-interest" placeholder="0.00" />
              </div>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={repay.date} onChange={(e) => setR("date", e.target.value)} className="mt-1.5" data-testid="repay-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepayOpen(false)}>Cancel</Button>
            <Button onClick={submitRepay} data-testid="repay-submit">Log repayment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
